import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@radix-ui/react-dialog';
import { clsx } from 'clsx';
import {
  ComponentPropsWithRef,
  ComponentPropsWithoutRef,
  FC,
  ReactNode,
} from 'react';
import { IconButton } from '../button/icon-button';
import { XmarkIcon } from '../icons/react/xmark';
import {
  modalClose,
  modalContent,
  modalContentContainer,
  modalHeader,
  modalHeaderStepper,
  modalHeaderWithCloseIcon,
  modalOverlay,
  modalTitle,
} from './modal.css';

export const ModalRoot = Dialog;

export const ModalTrigger: FC<ComponentPropsWithRef<typeof DialogTrigger>> = ({
  children,
  ...props
}) => (
  <DialogTrigger onClick={(e) => e.stopPropagation()} {...props}>
    {children}
  </DialogTrigger>
);

type DialogContentInteractOutsideEvent = Parameters<
  NonNullable<
    ComponentPropsWithoutRef<typeof DialogContent>['onInteractOutside']
  >
>[0];

export const ModalContent = function ModalContentComponent({
  ref: forwardedRef,
  children,
  className,
  contentClassName,
  overlayClassName,
  preventOutsideClose,
  staticOverlay,
  onInteractOutside,
  ...props
}: ComponentPropsWithRef<typeof DialogContent> & {
  contentClassName?: string;
  overlayClassName?: string;
  preventOutsideClose?: boolean;
  /** Radix drops the overlay on a non-modal dialog; this one never takes a click. */
  staticOverlay?: boolean;
}) {
  const handleInteractOutside = (event: DialogContentInteractOutsideEvent) => {
    const target = event.target;
    if (target instanceof Element && target.closest('[data-combobox-popup]')) {
      event.preventDefault();
    }
    if (preventOutsideClose) {
      event.preventDefault();
    }
    onInteractOutside?.(event);
  };

  return (
    <DialogPortal>
      {staticOverlay ? (
        <div
          aria-hidden
          className={clsx(modalOverlay, overlayClassName)}
          style={{ pointerEvents: 'none' }}
        />
      ) : (
        <DialogOverlay className={clsx(modalOverlay, overlayClassName)} />
      )}

      <DialogContent
        aria-describedby={undefined}
        className={clsx(modalContentContainer, className)}
        onInteractOutside={handleInteractOutside}
        {...props}
        ref={forwardedRef}
      >
        <div className={clsx(modalContent, contentClassName)}>{children}</div>
      </DialogContent>
    </DialogPortal>
  );
};

export const ModalHeader = function ModalHeaderComponent({
  ref,
  children,
  className,
  withoutCloseIcon,
  inversedClose,
  ...rest
}: ComponentPropsWithRef<'div'> & {
  withoutCloseIcon?: boolean;
  inversedClose?: boolean;
}) {
  return (
    <div
      ref={ref}
      className={clsx(
        modalHeader,
        { [modalHeaderWithCloseIcon]: !withoutCloseIcon },
        className,
      )}
      {...rest}
    >
      {children}
      {withoutCloseIcon ? null : (
        <ModalClose className={modalClose} asChild>
          <IconButton variant="ghost" inversed={inversedClose}>
            <XmarkIcon />
          </IconButton>
        </ModalClose>
      )}
    </div>
  );
};

export const ModalStepper = function ModalStepperComponent({
  ref,
  className,
  step,
  totalSteps,
  ...rest
}: ComponentPropsWithRef<'div'> & {
  step: number;
  totalSteps?: number;
}) {
  return (
    <div ref={ref} className={clsx(modalHeaderStepper, className)} {...rest}>
      Step {step}
      {totalSteps ? ` of ${totalSteps}` : null}
    </div>
  );
};

export const ModalClose = function ModalCloseComponent({
  ref,
  children,
  className,
  ...props
}: ComponentPropsWithRef<typeof DialogClose>) {
  return (
    <DialogClose ref={ref} className={className} aria-label="Close" {...props}>
      {children}
    </DialogClose>
  );
};

export const ModalTitle = function ModalTitleComponent({
  ref: forwardedRef,
  startIcon,
  children,
  className,
  ...props
}: ComponentPropsWithRef<typeof DialogTitle> & {
  startIcon?: ReactNode;
}) {
  return (
    <DialogTitle
      className={clsx(modalTitle, className)}
      {...props}
      ref={forwardedRef}
    >
      {startIcon}
      {children}
    </DialogTitle>
  );
};
